import { useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  Type, LayoutTemplate, FileUp, FileDown, FileSignature, FileArchive, Images,
  Tag, FileSearch, ArrowLeft, Search, Star,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import RichTextTab from "../components/studio/RichTextTab";
import TemplatesTab from "../components/studio/TemplatesTab";
import ConverterTab from "../components/studio/ConverterTab";
import ReverseTab from "../components/studio/ReverseTab";
import AgreementsTab from "../components/studio/AgreementsTab";
import CompressTab from "../components/studio/CompressTab";
import CarouselTab from "../components/studio/CarouselTab";
import SearchablePdfTab from "../components/studio/SearchablePdfTab";
import WelcomeTour from "../components/studio/WelcomeTour";

const EMERALD = "bg-emerald-50 text-emerald-700 border-emerald-200";
const INDIGO = "bg-indigo-50 text-indigo-700 border-indigo-200";
const AMBER = "bg-amber-50 text-amber-800 border-amber-200";

const TOOLS = [
  { id: "editor", name: "Rich Text", category: "Create", Icon: Type,
    desc: "Write polished documents, memos & reports with a live A4 preview.", badge: "1 credit", badgeColor: EMERALD, testid: "tool-card-rich-text" },
  { id: "templates", name: "Templates", category: "Create", Icon: LayoutTemplate,
    desc: "Invoices, resumes, certificates, cover letters & reports in seconds.", badge: "1 credit", badgeColor: EMERALD, testid: "tool-card-templates" },
  { id: "converter", name: "File → PDF", category: "Convert", Icon: FileUp,
    desc: "Turn Word, Excel, PowerPoint & images into clean PDFs.", badge: "from 1 credit", badgeColor: INDIGO, testid: "tool-card-file-to-pdf" },
  { id: "reverse", name: "PDF → Word / Images", category: "Convert", Icon: FileDown,
    desc: "Extract an editable Word doc or high-res images from any PDF.", badge: "2 credits", badgeColor: INDIGO, testid: "tool-card-pdf-to-word" },
  { id: "searchable", name: "Make Searchable", category: "Convert", Icon: FileSearch,
    desc: "OCR scanned PDFs into a searchable, selectable text layer.", badge: "OCR · 3 credits", badgeColor: AMBER, testid: "tool-card-ocr" },
  { id: "agreements", name: "Agreements", category: "Creator Tools", Icon: FileSignature,
    desc: "Draft clean contracts, NDAs & letters from guided fields.", badge: "1 credit", badgeColor: EMERALD, testid: "tool-card-agreements" },
  { id: "compress", name: "Compress", category: "Creator Tools", Icon: FileArchive,
    desc: "Shrink oversized PDFs for fast email & web sharing.", badge: "1 credit", badgeColor: EMERALD, testid: "tool-card-compress" },
  { id: "carousel", name: "Social Carousel", category: "Creator Tools", Icon: Images,
    desc: "Turn a PDF into Instagram / LinkedIn image carousel slides.", badge: "2 credits", badgeColor: INDIGO, testid: "tool-card-social-carousel" },
];

const CATEGORIES = ["All", "Create", "Convert", "Creator Tools"];

export default function Studio() {
  const location = useLocation();
  const { user } = useAuth();
  const source = location.state?.reedit || null;
  const inWorkspace = !!user?.active_workspace;

  const rtInitial = source?.type === "richtext" ? { title: source.title, html: source.html } : null;
  const tplInitial = source?.type === "template" ? { template_type: source.template_type, data: source.data } : null;

  const [active, setActive] = useState(source ? (source.type === "richtext" ? "editor" : "templates") : null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const [searchParams, setSearchParams] = useSearchParams();

  // Open a specific tool when deep-linked from the homepage (?tool=) or after sign-in (localStorage).
  useEffect(() => {
    const wanted = searchParams.get("tool") || localStorage.getItem("facio_open_tool");
    if (wanted && TOOLS.some((t) => t.id === wanted)) {
      setActive(wanted);
    }
    if (localStorage.getItem("facio_open_tool")) localStorage.removeItem("facio_open_tool");
    if (searchParams.get("tool")) {
      searchParams.delete("tool");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usage = user?.tool_usage || {};
  const activeTool = TOOLS.find((t) => t.id === active);
  const q = query.trim().toLowerCase();
  const filtered = TOOLS.filter(
    (t) => (cat === "All" || t.category === cat) &&
      (!q || t.name.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q))
  );
  const ordered = [...filtered].sort(
    (a, b) => (usage[b.id] || 0) - (usage[a.id] || 0) || TOOLS.indexOf(a) - TOOLS.indexOf(b)
  );
  const topToolId = !q && (usage[ordered[0]?.id] || 0) > 0 ? ordered[0].id : null;
  const recentTools = (user?.recent_tools || [])
    .map((id) => TOOLS.find((t) => t.id === id))
    .filter(Boolean)
    .slice(0, 4);

  const renderTool = (id) => {
    switch (id) {
      case "editor": return <RichTextTab key={rtInitial ? "reedit" : "new"} initial={rtInitial} />;
      case "templates": return <TemplatesTab key={tplInitial ? "reedit" : "new"} initial={tplInitial} />;
      case "converter": return <ConverterTab />;
      case "reverse": return <ReverseTab />;
      case "searchable": return <SearchablePdfTab />;
      case "agreements": return <AgreementsTab />;
      case "compress": return <CompressTab />;
      case "carousel": return <CarouselTab />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      {user?.terms_accepted_version === user?.policy_version && <WelcomeTour />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="studio-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Creator Studio</h1>
            <p className="text-slate-600 mt-1.5 text-sm max-w-2xl" data-testid="studio-cost-hint">
              {source ? "Editing a copy of your document. " : ""}
              {inWorkspace
                ? <>Team workspace <span className="font-semibold">"{user.active_workspace.name}"</span> — costs come from the shared pool, +{user?.pricing?.team_premium_applied ?? 1} team credit per export. </>
                : <>Pick a tool to start. You only pay when you unlock a file — each tool shows its cost up front. </>}
              <Link to="/pricing" data-testid="studio-pricing-link" className="text-indigo-600 font-medium hover:underline inline-flex items-center gap-1">
                <Tag className="h-3.5 w-3.5" /> Pricing & limits
              </Link>
            </p>
          </div>
        </div>

        {/* Banners */}
        {inWorkspace && user?.can_export === false && (
          <div data-testid="viewer-banner" className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            You have <strong>view-only</strong> access in this workspace. Ask the owner for editor access to create and export documents.
          </div>
        )}
        {inWorkspace && user?.active_workspace?.low && user?.can_export !== false && (
          <div data-testid="low-pool-banner" className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 flex items-center justify-between gap-3">
            <span>⚠️ This team's credit pool is low (<strong>{user.active_workspace.credits} credits</strong> left).</span>
            {user.active_workspace.is_owner && (
              <a href="/buy-credits" className="shrink-0 font-semibold underline hover:no-underline">Top up pool</a>
            )}
          </div>
        )}

        {activeTool ? (
          <div data-testid="tool-active-view">
            <button
              data-testid="back-to-tools-button"
              onClick={() => setActive(null)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 hover:bg-indigo-50 text-slate-700 hover:text-indigo-600 font-medium text-sm transition-colors border border-slate-200 mb-6 group"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" /> All tools
            </button>
            {renderTool(activeTool.id)}
          </div>
        ) : (
          <div data-testid="tool-launcher">
            {recentTools.length > 0 && !q && cat === "All" && (
              <div data-testid="jump-back-in" className="mb-8">
                <p className="text-sm font-semibold text-slate-500 mb-3">Jump back in</p>
                <div className="flex flex-wrap gap-3">
                  {recentTools.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`recent-tool-${t.id}`}
                      onClick={() => setActive(t.id)}
                      className="group inline-flex items-center gap-2.5 pl-2.5 pr-4 py-2 bg-white/90 border border-slate-200 rounded-full hover:border-indigo-400 hover:shadow-md transition-all"
                    >
                      <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white flex items-center justify-center transition-colors">
                        <t.Icon className="h-4 w-4" />
                      </span>
                      <span className="text-sm font-medium text-slate-800">{t.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Search + category filter */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pb-6 border-b border-slate-200/80 mb-8">
              <div className="relative flex-1 max-w-sm">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="tool-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search tools…"
                  className="bg-white/90 pl-9 rounded-xl"
                />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    data-testid={`tool-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setCat(c)}
                    className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      cat === c ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Tool grid */}
            {ordered.length === 0 ? (
              <div className="bg-white/90 border border-slate-200 rounded-2xl p-12 text-center text-slate-500" data-testid="tool-no-results">
                No tools match "{query}".
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {ordered.map((t) => (
                  <button
                    key={t.id}
                    data-testid={t.testid}
                    onClick={() => setActive(t.id)}
                    className="group relative flex flex-col justify-between p-6 bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 hover:border-indigo-400/60 transition-all duration-200 hover:-translate-y-1 cursor-pointer overflow-hidden text-left"
                  >
                    <span className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-indigo-600 to-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <div>
                      <div className="w-12 h-12 rounded-xl bg-indigo-50/80 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white flex items-center justify-center transition-colors duration-200 mb-4">
                        <t.Icon className="h-6 w-6" />
                      </div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <h3 className="font-display text-lg font-semibold text-slate-900">{t.name}</h3>
                        {t.id === topToolId && (
                          <span data-testid="most-used-badge" className="inline-flex items-center gap-1 text-[10px] font-mono font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full">
                            <Star className="h-2.5 w-2.5 fill-indigo-500 text-indigo-500" /> Most used
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 leading-relaxed">{t.desc}</p>
                    </div>
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
                      <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">{t.category}</span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-mono font-medium border ${t.badgeColor}`}>
                        {t.badge}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
